import {
  Confidences,
  ScrapEyesEvent,
} from "components/scrapEyeLabelling/types";
import dayjs, { Dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";
import {
  danglingWindowEdgeEvents,
  eventStartsInPeriod,
  groupLiftDropSequences,
} from "./editScope";
import { sorted } from "helpers";
import {
  DefaultAreas,
  EventType,
  LabelData,
  LabelType,
  ReviewPeriodData,
} from "src/store/api/generatedApi";
import { parseFiniteNumber } from "src/utils/parseFiniteNumber";

import { v4 as uuidv4 } from "uuid";

dayjs.extend(utc);

export const findLabelValue = (
  labels: LabelData[],
  labelType: LabelType
): string | null => {
  return labels.find((label) => label.label_type === labelType)?.label ?? null;
};

export interface ExtractedLabels {
  material: string | null;
  coMaterial: string | null;
  area: string | null;
  comment: string | undefined;
  mass: string | null;
  initialMass: number | null;
  finalMass: number | null;
  confidences: Confidences | undefined;
}

const extractConfidences = (labels: LabelData[]): Confidences | undefined => {
  const event = findLabelValue(labels, "event_confidence");
  const material = findLabelValue(labels, "material_confidence");
  const area = findLabelValue(labels, "area_confidence");
  if (!event && !material && !area) return undefined;
  return {
    ...(event ? { event: Number(event) } : {}),
    ...(material ? { material: Number(material) } : {}),
    ...(area ? { area: Number(area) } : {}),
  };
};

export const extractLabels = (
  labels: LabelData[],
  eventType: EventType,
  defaultAreas: DefaultAreas
): ExtractedLabels => {
  const material = findLabelValue(labels, "material");
  const coMaterial = findLabelValue(labels, "co_material");
  const areaLabel = findLabelValue(labels, "area");
  const area = areaLabel ?? defaultAreas[eventType];
  const comment = findLabelValue(labels, "comment") ?? undefined;
  const mass = findLabelValue(labels, "mass");
  const initialMass = parseFiniteNumber(findLabelValue(labels, "initial_mass"));
  const finalMass = parseFiniteNumber(findLabelValue(labels, "final_mass"));
  const confidences = extractConfidences(labels);

  return {
    material,
    coMaterial,
    area,
    comment,
    mass,
    initialMass,
    finalMass,
    confidences,
  };
};

export const isMassEventDetached = ({
  hasMatchedMassEvent,
  mass,
}: {
  hasMatchedMassEvent: boolean;
  mass: string | null;
}): boolean => hasMatchedMassEvent && mass === null;

/**
 * Inverse of `extractLabels`: turn an event's label-bearing fields into LabelData
 * rows. Only fields present on the input are emitted, so passing a partial (e.g.
 * just the fields a reviewer changed) yields labels for exactly those fields.
 */
export const toLabelData = (event: Partial<ScrapEyesEvent>): LabelData[] => {
  const labels: LabelData[] = [];
  if (event.material) {
    labels.push({ label_type: "material", label: event.material });
  }
  // A co-material label is only valid alongside a distinct primary material
  // (enforced server-side); drop an orphaned or duplicate co-material so it
  // can never 422 the whole verify batch.
  if (
    event.material &&
    event.coMaterial &&
    event.coMaterial !== event.material
  ) {
    labels.push({ label_type: "co_material", label: event.coMaterial });
  }
  if (event.comment) {
    labels.push({ label_type: "comment", label: event.comment });
  }
  if (event.area) {
    labels.push({ label_type: "area", label: event.area });
  }
  if (event.mass) {
    labels.push({ label_type: "mass", label: event.mass });
  }
  if (event.initialMass != null) {
    labels.push({
      label_type: "initial_mass",
      label: String(event.initialMass),
    });
  }
  if (event.finalMass != null) {
    labels.push({ label_type: "final_mass", label: String(event.finalMass) });
  }
  return labels;
};

const shortLabel = (event: ScrapEyesEvent, defaultAreas: DefaultAreas) => {
  if (!event.area) return undefined;
  if (event.area === defaultAreas[event.eventType]) {
    return undefined;
  }
  return event.areaLabel;
};

export const addEventMetadata = <T extends ScrapEyesEvent>(
  events: T[],
  defaultAreas: DefaultAreas
): T[] => {
  const eventsWithShortLabel = events.map((event) => {
    return { ...event, shortLabel: shortLabel(event, defaultAreas) };
  });

  // A lift is paired with the drop that immediately follows it (one basket
  // movement), regardless of their material labels — a mismatch between the two
  // is a data error to surface, not a reason to leave them unassociated.
  const liftsPaired = eventsWithShortLabel.map((event, index) => {
    if (event.pairId !== null || event.eventType !== "lift") return event;

    const nextEvent = events[index + 1];
    if (nextEvent && nextEvent.eventType === "drop" && !nextEvent.pairId) {
      const pairId = uuidv4();
      return { ...event, pairId };
    }

    return event;
  });

  const pairedEvents = liftsPaired.map((event, index) => {
    if (event.eventType === "drop" && !event.pairId) {
      const prevEvent = liftsPaired[index - 1];
      if (prevEvent && prevEvent.eventType === "lift" && prevEvent.pairId) {
        return { ...event, pairId: prevEvent.pairId };
      }
    }
    return event;
  });

  const pairIndex = pairedEvents.reduce((pairs, event) => {
    if (!event.pairId) return pairs;
    const pair = pairs.get(event.pairId);
    if (!pair) {
      pairs.set(event.pairId, { first: event });
    } else if (!pair.firstWithDifferentId && event.id !== pair.first.id) {
      pairs.set(event.pairId, { ...pair, firstWithDifferentId: event });
    }
    return pairs;
  }, new Map<string, { first: T; firstWithDifferentId?: T }>());

  return pairedEvents.map((event) => {
    if (!event.pairId) return event;
    const pair = pairIndex.get(event.pairId);
    const partner =
      pair?.first.id === event.id ? pair.firstWithDifferentId : pair?.first;
    const materialsDisagree =
      partner != null &&
      event.material !== null &&
      partner.material !== null &&
      event.material !== partner.material;
    if (!materialsDisagree) return event;
    const warnings = event.warnings ?? [];
    return warnings.includes("materialMismatchWithPair")
      ? event
      : { ...event, warnings: [...warnings, "materialMismatchWithPair"] };
  });
};

export const filterReviewPeriods = (
  reviewPeriods: ReviewPeriodData[] | undefined,
  viewingWindowStart: Dayjs,
  viewingWindowEnd: Dayjs
): ReviewPeriodData[] => {
  if (!reviewPeriods) return [];

  return sorted(
    reviewPeriods.filter((period) => {
      const periodStart = dayjs(period.start);
      const periodEnd = dayjs(period.end);
      return (
        periodStart.isBefore(viewingWindowEnd) &&
        periodEnd.isAfter(viewingWindowStart)
      );
    }),
    (period) => period.start
  );
};

export const getNextEvent = (
  events: ScrapEyesEvent[] | undefined,
  currentTime: number
): ScrapEyesEvent | undefined => {
  if (!events || events.length === 0) return undefined;

  return events.find((event) => event.start.unix() > currentTime);
};

/** The review period whose `[start, end)` window covers `timestamp`, if any. */
export const reviewPeriodContaining = (
  timestamp: Dayjs,
  reviewPeriods: ReviewPeriodData[]
): ReviewPeriodData | undefined =>
  reviewPeriods.find((period) =>
    eventStartsInPeriod({
      start: timestamp,
      periodStart: dayjs(period.start),
      periodEnd: dayjs(period.end),
    })
  );

// A sequence member from `events` (flagged and returned) or from `contextEvents`
// (grouped for context only, no id).
interface SequenceMember {
  id?: string;
  start: Dayjs;
  end: Dayjs;
  eventType: EventType;
}

/**
 * Flags every event of a lift/drop charge sequence (see `groupLiftDropSequences`)
 * whose events fall across more than one review period. `contextEvents` widen the
 * pool a sequence is grouped from — e.g. a lone `events` member whose partner is only
 * loaded as context still finds it — without being flagged or returned themselves.
 */
export const flagCrossPeriodPairs = <T extends ScrapEyesEvent>({
  events,
  contextEvents,
  reviewPeriods,
  maxSequenceGapSeconds,
}: {
  events: T[];
  contextEvents: { start: Dayjs; end: Dayjs; eventType: EventType }[];
  reviewPeriods: ReviewPeriodData[];
  maxSequenceGapSeconds: number;
}): T[] => {
  const parsedPeriods = reviewPeriods.map((period) => ({
    id: period.id,
    start: dayjs(period.start),
    end: dayjs(period.end),
  }));
  const periodContaining = (timestamp: Dayjs) =>
    parsedPeriods.find((period) =>
      eventStartsInPeriod({
        start: timestamp,
        periodStart: period.start,
        periodEnd: period.end,
      })
    );

  const pool: SequenceMember[] = [
    ...events.map((event) => ({
      id: event.id,
      start: event.start,
      end: event.end,
      eventType: event.eventType,
    })),
    ...contextEvents,
  ];

  const crossingEventIds = new Set(
    groupLiftDropSequences({ events: pool, maxSequenceGapSeconds })
      .filter((sequence) => {
        const periodIds = new Set(
          sequence.flatMap((event) => {
            const period = periodContaining(event.start);
            return period ? [period.id] : [];
          })
        );
        return periodIds.size > 1;
      })
      .flatMap((sequence) =>
        sequence.flatMap((event) => (event.id !== undefined ? [event.id] : []))
      )
  );

  return events.map((event) =>
    crossingEventIds.has(event.id)
      ? { ...event, pairCrossesReviewPeriod: true }
      : event
  );
};

/**
 * Flags an unpaired lift or drop whose partner falls outside the loaded events (see
 * `danglingWindowEdgeEvents`) — the pairs a reviewer misses when only one half is
 * loaded. `contextEvents` widen the grouping pool the same way as `flagCrossPeriodPairs`.
 */
export const flagWindowEdgePairs = <T extends ScrapEyesEvent>({
  events,
  contextEvents,
  maxSequenceGapSeconds,
}: {
  events: T[];
  contextEvents: { start: Dayjs; end: Dayjs; eventType: EventType }[];
  maxSequenceGapSeconds: number;
}): T[] => {
  const pool: SequenceMember[] = [
    ...events.map((event) => ({
      id: event.id,
      start: event.start,
      end: event.end,
      eventType: event.eventType,
    })),
    ...contextEvents,
  ];
  const { danglingLifts, danglingDrops } = danglingWindowEdgeEvents({
    events: pool,
    maxSequenceGapSeconds,
  });
  const beyondWindowIds = new Set(
    [...danglingLifts, ...danglingDrops].flatMap((event) =>
      event.id !== undefined ? [event.id] : []
    )
  );
  return events.map((event) =>
    beyondWindowIds.has(event.id)
      ? { ...event, pairPartnerBeyondWindow: true }
      : event
  );
};

/** Formats a period as a UTC `HH:mm–HH:mm` range. */
export const formatUtcTimeRange = (start: Dayjs, end: Dayjs): string =>
  `${start.utc().format("HH:mm")}–${end.utc().format("HH:mm")}`;
